import React, { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import Header from './Header';
import Footer from './Footer';
import { useUser } from '../contexts/UserContext';
import './BlogIndex.css';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5001';

export const BLOG_CATEGORIES = [
  'All',
  'Interview Prep',
  'Job Position',
  'Interview Questions',
  'Resume & Cover Letters',
  'Technical Topic',
  'AI Tools & Productivity',
  'General Topic',
];

function formatDate(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  } catch { return ''; }
}

function Avatar({ name, src, size = 24 }) {
  const initial = (name || '?').trim().charAt(0).toUpperCase();
  if (src) {
    return <img src={src} alt={name || ''} className="blog-avatar-img" style={{ width: size, height: size }} />;
  }
  return (
    <span className="blog-avatar-fallback" style={{ width: size, height: size, fontSize: Math.round(size * 0.45) }}>
      {initial}
    </span>
  );
}

function BlogCard({ post }) {
  const hasImg = !!post.hero_image_url;
  return (
    <Link to={`/blog/${post.slug}`} className="blog-card">
      <div
        className={`blog-card-hero ${hasImg ? 'with-img' : ''}`}
      >
        {hasImg && <img src={post.hero_image_url} alt="" loading="lazy" />}
        <span className="blog-card-hero-tag">{post.category}</span>
      </div>
      <div className="blog-card-body">
        <div className="blog-card-meta">
          <span>⏱ {post.read_minutes || 1} min</span>
          <span className="blog-card-dot">·</span>
          <span>{formatDate(post.published_at)}</span>
        </div>
        <h3 className="blog-card-title">{post.title}</h3>
        {post.summary && <p className="blog-card-summary">{post.summary}</p>}
        <div className="blog-card-author">
          <Avatar name={post.author_name} src={post.author_avatar_url} />
          <span className="blog-card-author-name">{post.author_name}</span>
        </div>
      </div>
    </Link>
  );
}

export default function BlogIndex() {
  const { user } = useUser();
  const [searchParams, setSearchParams] = useSearchParams();
  const [posts, setPosts] = useState([]);
  const [total, setTotal] = useState(0);
  const [authors, setAuthors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const category = searchParams.get('category') || 'All';
  const author = searchParams.get('author') || '';
  const q = searchParams.get('q') || '';
  const sort = searchParams.get('sort') || 'newest';

  const [searchInput, setSearchInput] = useState(q);
  useEffect(() => { setSearchInput(q); }, [q]);

  const queryString = useMemo(() => {
    const p = new URLSearchParams();
    if (category && category !== 'All') p.set('category', category);
    if (author) p.set('author', author);
    if (q) p.set('q', q);
    if (sort) p.set('sort', sort);
    return p.toString();
  }, [category, author, q, sort]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError('');
    fetch(`${API_BASE_URL}/api/blog?${queryString}`)
      .then(r => r.json())
      .then(data => {
        if (cancelled) return;
        setPosts(data.posts || []);
        setTotal(data.total || 0);
        setAuthors(data.authors || []);
      })
      .catch(() => !cancelled && setError('Could not load posts. Try refreshing.'))
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, [queryString]);

  const updateParam = (key, value) => {
    const next = new URLSearchParams(searchParams);
    if (value && value !== 'All') next.set(key, value);
    else next.delete(key);
    setSearchParams(next, { replace: true });
  };

  const onSearchSubmit = (e) => {
    e.preventDefault();
    updateParam('q', searchInput.trim());
  };

  return (
    <div className="blog-index-page">
      {/* Ambient animated background — three drifting blobs + faint starfield.
          Sits behind everything (z-index: 0) while content uses z-index: 1. */}
      <div className="blog-bg-stars" aria-hidden="true" />
      <div className="blog-bg-orb" aria-hidden="true" />

      <Header />

      <main className="blog-index-main">
        <section className="blog-hero">
          <span className="blog-hero-eyebrow">Terview Blog</span>
          <h1 className="blog-hero-title">
            Win the <span className="accent">job market</span>.
          </h1>
          <p className="blog-hero-sub">
            Fresh writing on interviews, resumes, and AI tools that get you hired —
            no fluff, no listicles, no LinkedIn-bro energy.
          </p>
        </section>

        {/* Top toolbar: search + author + sort + count + admin CTA */}
        <div className="blog-toolbar">
          <form onSubmit={onSearchSubmit} className="blog-search">
            <span className="blog-search-icon">🔍</span>
            <input
              type="text"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Search posts…"
            />
          </form>
          <select
            className="blog-filter-select"
            value={author}
            onChange={(e) => updateParam('author', e.target.value)}
          >
            <option value="">All authors</option>
            {authors.map(a => (
              <option key={a.id} value={a.id}>{a.name}</option>
            ))}
          </select>
          <select
            className="blog-filter-select"
            value={sort}
            onChange={(e) => updateParam('sort', e.target.value)}
          >
            <option value="newest">Newest first</option>
            <option value="oldest">Oldest first</option>
          </select>
          <div className="blog-toolbar-meta">
            <span className="blog-index-count">
              {posts.length} of {total}
            </span>
            {user?.is_admin && (
              <Link to="/blog/admin/new" className="blog-admin-cta">
                + New post
              </Link>
            )}
          </div>
        </div>

        {/* Category chips, inline (no separate card) */}
        <div className="blog-category-chips">
          {BLOG_CATEGORIES.map(c => (
            <button
              key={c}
              type="button"
              className={`blog-chip ${category === c ? 'active' : ''}`}
              onClick={() => updateParam('category', c)}
            >
              {c}
            </button>
          ))}
        </div>

        {/* Card grid */}
        <div className="blog-cards">
          {loading ? (
            <div className="blog-state">Loading posts…</div>
          ) : error ? (
            <div className="blog-state error">{error}</div>
          ) : posts.length === 0 ? (
            <div className="blog-state">
              No posts found{q ? ` for "${q}"` : ''}.
            </div>
          ) : (
            posts.map(p => <BlogCard key={p.id} post={p} />)
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
}
